import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Navigation } from "lucide-react";
import { toast } from "sonner";
import { MaskedLine, FadeUp, SectionLabel } from "@/components/Reveal";
import MagneticButton from "@/components/MagneticButton";
import { submitContact } from "@/lib/api";

const PROJECT_TYPES = [
    "Private Villa",
    "Interior Design",
    "Construction",
    "Planning",
    "Renovation",
    "Commercial",
    "Hospitality",
    "Consulting",
    "Other",
];

const inputCls =
    "w-full rounded-lg border border-white/10 bg-coal/60 px-4 py-3.5 text-sm text-white placeholder:text-ash/60 outline-none transition-colors duration-300 focus:border-gold/60";

export default function CTA() {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const preType = searchParams.get("type");
    const fromServices = searchParams.get("from") === "services";
    const initialType =
        preType && PROJECT_TYPES.includes(preType)
            ? preType
            : PROJECT_TYPES[0];

    const [form, setForm] = useState({
        name: "",
        email: "",
        project_type: initialType,
        message: "",
    });
    const [sending, setSending] = useState(false);

    // Re-sync project_type when navigating with a new ?type= query
    useEffect(() => {
        if (preType && PROJECT_TYPES.includes(preType)) {
            setForm((f) => ({ ...f, project_type: preType }));
        }
    }, [preType]);

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await submitContact(form);
            toast.success(
                "Received. A senior partner will reply within one working day.",
            );
            setForm({
                name: "",
                email: "",
                project_type: PROJECT_TYPES[0],
                message: "",
            });
        } catch {
            toast.error("Something went wrong — please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative overflow-clip py-32 sm:py-44"
        >
            <div className="pointer-events-none absolute -right-40 top-0 h-[560px] w-[560px] rounded-full bg-gold/[0.05] blur-[120px]" />

            <div className="relative mx-auto grid max-w-7xl gap-20 px-6 sm:px-10 lg:grid-cols-2">
                <div>
                    <SectionLabel index="07" title="Begin" />
                    {fromServices && (
                        <FadeUp delay={0.05} className="mt-6">
                            <Link
                                to="/services"
                                data-testid="back-to-services-chip"
                                className="group inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/[0.06] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold transition-all duration-300 hover:border-gold hover:bg-gold hover:text-coal"
                            >
                                <ArrowLeft
                                    size={12}
                                    className="transition-transform duration-300 group-hover:-translate-x-0.5"
                                />
                                Back to Services
                            </Link>
                        </FadeUp>
                    )}
                    <h2 className="mt-10 font-display text-[clamp(2rem,9vw,3.75rem)] font-black uppercase leading-[0.95] tracking-tight [word-break:break-word] sm:text-5xl md:text-6xl">
                        <MaskedLine delay={0.05}>Let's Build</MaskedLine>
                        <MaskedLine delay={0.15}>
                            <span className="text-outline">Something</span>
                        </MaskedLine>
                        <MaskedLine delay={0.25}>
                            <span className="text-gold">Extraordinary</span>
                        </MaskedLine>
                    </h2>

                    <FadeUp delay={0.35} className="mt-12 max-w-md">
                        <p className="text-sm leading-relaxed text-ash">
                            Tell us about the site, the ambition and the timeline.
                            Every enquiry is read by a founding partner — never a
                            bot, never a queue.
                        </p>
                    </FadeUp>

                    <div className="mt-14 space-y-5">
                        {[
                            { icon: MapPin, text: "Kollam, Kerala, India" },
                        ].map(({ icon: Icon, text }) => (
                            <FadeUp key={text} delay={0.4}>
                                <p className="flex items-center gap-4 text-sm text-ash">
                                    <span className="flex h-9 w-9 items-center justify-center border border-white/10 text-gold">
                                        <Icon size={15} strokeWidth={1.5} />
                                    </span>
                                    {text}
                                </p>
                            </FadeUp>
                        ))}
                    </div>

                    <FadeUp delay={0.45} className="mt-8">
                        <div
                            data-testid="contact-map"
                            className="relative h-56 overflow-hidden rounded-2xl border border-white/[0.08]"
                        >
                            <iframe
                                title="ate.teen studio — Kollam, Kerala"
                                src="https://www.google.com/maps?q=Kollam,+Kerala,+India&z=12&output=embed"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="h-full w-full border-0 grayscale invert-[0.92] hue-rotate-180 contrast-[0.85]"
                            />
                            <div className="pointer-events-none absolute inset-0 rounded-2xl border border-gold/20" />
                        </div>
                        <a
                            data-testid="contact-directions-btn"
                            href="https://www.google.com/maps/dir/?api=1&destination=Kollam%2C+Kerala%2C+India"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Get directions to ate.teen studio on Google Maps"
                            className="group mt-5 inline-flex items-center gap-3 rounded-full bg-gold px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-coal shadow-[0_0_28px_-6px_rgba(200,169,106,0.55)] transition-all duration-300 hover:bg-white hover:shadow-[0_0_40px_-4px_rgba(200,169,106,0.85)]"
                        >
                            <Navigation
                                size={14}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                            Get Directions
                            <ArrowUpRight
                                size={14}
                                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            />
                        </a>
                    </FadeUp>
                </div>

                <FadeUp delay={0.2} y={60}>
                    <form
                        data-testid="contact-form"
                        onSubmit={onSubmit}
                        className="glass rounded-2xl p-8 sm:p-10"
                    >
                        <div className="grid gap-5">
                            <div>
                                <label
                                    htmlFor="contact-name"
                                    className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-ash"
                                >
                                    Name
                                </label>
                                <input
                                    id="contact-name"
                                    data-testid="contact-name-input"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your full name"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="contact-email"
                                    className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-ash"
                                >
                                    Email
                                </label>
                                <input
                                    id="contact-email"
                                    data-testid="contact-email-input"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@example.com"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="contact-type"
                                    className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-ash"
                                >
                                    Project Type
                                </label>
                                <select
                                    id="contact-type"
                                    data-testid="contact-type-select"
                                    value={form.project_type}
                                    onChange={set("project_type")}
                                    className={`${inputCls} appearance-none`}
                                >
                                    {PROJECT_TYPES.map((t) => (
                                        <option key={t} value={t} className="bg-onyx">
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label
                                    htmlFor="contact-message"
                                    className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.3em] text-ash"
                                >
                                    The Vision
                                </label>
                                <textarea
                                    id="contact-message"
                                    data-testid="contact-message-input"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Site, scale, timeline, ambition…"
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                            <MagneticButton className="mt-2 w-full">
                                <button
                                    data-testid="contact-submit-button"
                                    type="submit"
                                    disabled={sending}
                                    className="group flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-coal transition-all duration-300 hover:bg-white disabled:opacity-60"
                                >
                                    {sending ? "Sending…" : "Start the Conversation"}
                                    <ArrowUpRight
                                        size={16}
                                        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                    />
                                </button>
                            </MagneticButton>
                        </div>
                    </form>
                </FadeUp>
            </div>
        </section>
    );
}
